'use client';

/**
 * ExplorerSearchContext — shared state for narrative-search explorers.
 *
 * Mirrors `<ChatSurfaceProvider />` for the chat surface. The context
 * exposes a stable shape that the *generic* search-flow sections —
 * `<SearchBarSection />`, `<ProjectCardSection />`,
 * `<ClarificationSection />`, `<SearchResultsSection />`,
 * `<EmptyStateSection />` — read from. Each mode that wants the
 * narrative-search flow (today: agents, obeya simulations) provides a
 * concrete provider that wires the actual backend (typically `ki_ask`).
 *
 * Generic sections never call backends directly; they always go through
 * this context. That keeps the section components mode-agnostic and
 * lets future modes (e.g. `tenders` with a `tender_ask` strategy) plug
 * the same UI into a different backend by writing their own provider.
 *
 * `useExplorerSearch()` returns `null` when the section tree isn't
 * wrapped in a provider — the generic sections degrade gracefully
 * (e.g. `SearchBarSection` becomes a disabled input) rather than
 * throwing, so `<SchemaExplorer />` can keep rendering for filter-style
 * modes that don't opt in.
 *
 * @component_role: Explorer Surface State
 * @component_type: React Context
 * @domain: Frontend / Explorer
 * @layer: presentation
 */

import { createContext, useContext } from 'react';

export interface ExplorerSearchResult {
  semantic_id: string;
  name: string;
  entity_type: string;
  state: string | null;
}

export interface ExplorerProjectContext {
  semantic_id: string;
  name: string;
  rag_status: string | null;
  rag_label?: string;
  rfq_count?: number;
  po_count?: number;
}

export interface ExplorerSearchContextValue {
  // Composer
  query: string;
  setQuery: (q: string) => void;
  submit: () => Promise<void> | void;
  isLoading: boolean;
  error: string | null;

  // Result states (mutually exclusive in practice — provider sets one)
  clarification: string | null;
  searchResults: ExplorerSearchResult[];
  projectContext: ExplorerProjectContext | null;

  // Actions
  selectResult: (result: ExplorerSearchResult) => void;
  clearProject: () => void;

  /** Convenience predicate the EmptyStateSection uses to decide whether
   *  any sibling section is contributing content. Provider derives this
   *  from its own state; sections do NOT compute it themselves so the
   *  empty rule stays in one place. */
  hasContent: boolean;
}

const ExplorerSearchContext = createContext<ExplorerSearchContextValue | null>(null);

export function ExplorerSearchProvider({
  value,
  children,
}: {
  value: ExplorerSearchContextValue;
  children: React.ReactNode;
}) {
  return (
    <ExplorerSearchContext.Provider value={value}>
      {children}
    </ExplorerSearchContext.Provider>
  );
}

/** Read the explorer-search context. Returns null when no provider
 *  is mounted — sections decide how to degrade. */
export function useExplorerSearch(): ExplorerSearchContextValue | null {
  return useContext(ExplorerSearchContext);
}
